/**
 * Nội dung chuyển khoản & khớp giao dịch ngân hàng (GĐ9). Hàm thuần.
 *  - Phiếu đối soát: nội dung CK = mã phiếu (PDS-2610-00042) — bỏ dấu, ≤ 50 ký tự.
 *  - Trả nợ: "TRANO <mã đơn vị>" — SePay báo về, tự khớp theo mã đơn vị.
 * Ngân hàng hay bỏ/đổi ký tự đặc biệt (gạch nối, khoảng trắng), nên khớp trên chuỗi
 * chỉ còn chữ + số, in hoa.
 */

/** Không dấu, in hoa, chỉ chữ/số/khoảng trắng — chuẩn VietQR/NAPAS. */
export function toBankingUnaccent(str: string): string {
  if (!str) return ''
  return str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
}

/** Chuỗi so khớp: chỉ chữ + số, in hoa. */
export function matchKey(str: string | null | undefined): string {
  return toBankingUnaccent(str ?? '').replace(/ /g, '')
}

export function transferContent(text: string, max = 50): string {
  return toBankingUnaccent(text).slice(0, max)
}

export const SHIP_PREPAY_PREFIX = 'SHIP'

/** Nội dung CK shop trả trước tiền ship một đơn: "SHIP <mã đơn chỉ còn chữ + số>". */
export function shipPrepayContent(orderId: string): string {
  return transferContent(`${SHIP_PREPAY_PREFIX} ${matchKey(orderId)}`)
}

export const SHIP_BATCH_PREFIX = 'SHIPG'

/** Bảng chữ mã lệnh gộp: bỏ 0/O/1/I/L dễ đọc nhầm khi gõ tay vào app ngân hàng. */
const BATCH_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ'

/** Mã lệnh chuyển gộp ngẫu nhiên (8 ký tự). `rand` để test. */
export function shipBatchCode(rand: () => number = Math.random): string {
  let out = ''
  for (let i = 0; i < 8; i++) out += BATCH_CODE_ALPHABET[Math.floor(rand() * BATCH_CODE_ALPHABET.length) % BATCH_CODE_ALPHABET.length]
  return out
}

/**
 * Nội dung CK lệnh chuyển gộp tiền ship nhiều đơn: "SHIPG <mã lệnh>". Không trùng tiền tố với phiếu
 * từng đơn ("SHIP DH…" → khoá so khớp "SHIPDH…") nên gợi ý SePay không lẫn hai loại.
 */
export function shipBatchContent(code: string): string {
  return transferContent(`${SHIP_BATCH_PREFIX} ${matchKey(code)}`)
}

export const DEBT_PAYMENT_PREFIX = 'TRANO'

/** Nội dung CK đơn vị dùng khi trả nợ cho ShopTrack. */
export function debtPaymentContent(shopId: string): string {
  return transferContent(`${DEBT_PAYMENT_PREFIX} ${shopId}`)
}

/** Tìm đơn vị trả nợ trong nội dung CK — trả shopId khớp đầu tiên. */
export function findDebtPayer(content: string | null | undefined, shopIds: readonly string[]): string | null {
  const key = matchKey(content)
  if (!key.includes(DEBT_PAYMENT_PREFIX)) return null
  for (const id of shopIds) if (key.includes(DEBT_PAYMENT_PREFIX + matchKey(id))) return id
  return null
}

/** Nội dung CK có chứa mã phiếu (bỏ qua gạch nối / khoảng trắng / hoa-thường) không. */
export function contentHasCode(content: string | null | undefined, code: string): boolean {
  return matchKey(content).includes(matchKey(code))
}

/** Tìm mã phiếu PDS-YYMM-NNNNN trong nội dung CK (kể cả khi ngân hàng bỏ gạch nối). */
export function findVoucherCode(content: string | null | undefined): string | null {
  const m = /PDS(\d{4})(\d{5})/.exec(matchKey(content))
  return m ? `PDS-${m[1]}-${m[2]}` : null
}
